import { type ReactElement, useCallback, useState } from 'react';
import Select, {
  type SelectGroup,
  type SelectItem,
  type SelectProps,
} from '@fastnorth/kraken/components/select';
import { DivSkeleton } from '@fastnorth/kraken/components/layout';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../../hooks/AppProvider.tsx';
import useRaceSelectList, {
  type RaceSelectItem,
} from '../related-events-selector/hooks/useRaceSelectList.ts';
import { buildRaceUrl } from '../../utils/urlBuilder';
import type { KarwaUrlParams } from '../../views/types.ts';
import clearFilters from '../../views/leaderboard/components/utils/clearFilters.ts';
import { useResponsive } from '@fastnorth/kraken/components/page-layout';

type RaceSelectorProps = Pick<SelectProps, 'width' | 'onValueChange'> & {
  path?: string; // like /statistics or /awards
  selectGroups?: SelectGroup[];
  prependItems?: RaceSelectItem[];
  sortCompare?: (a: RaceSelectItem, b: RaceSelectItem) => number;
  visibleLabel?: boolean;
  loading?: boolean;
};

const defaultSortCompare = (a: SelectItem, b: SelectItem): number =>
  (a.sortOrder ?? 0) - (b.sortOrder ?? 0);

const RaceSelector = ({
  path = '',
  onValueChange,
  sortCompare = defaultSortCompare,
  selectGroups,
  visibleLabel = true,
  loading = false,
}: RaceSelectorProps): ReactElement | null => {
  const { t } = useTranslation('translation', { keyPrefix: 'karwa.event.race.results' });
  const { isMobile } = useResponsive();
  const [{ event, lastSelectedRace }, dispatch] = useApp();
  const { eventUniqueCode, raceId } = useParams<KarwaUrlParams>();
  const { search } = useLocation();
  const navigate = useNavigate();
  const defaultSelectList = useRaceSelectList(event?.subEvents ?? [], { sortCompare });
  const selectList = selectGroups ?? defaultSelectList;
  const hasRaces = selectList[0]?.items?.length;
  const selectDefaultWidth = isMobile ? 'full' : 'max-lg';

  const defaultValue = hasRaces
    ? selectList[0].items.find(({ value }) => [raceId, lastSelectedRace].indexOf(value) >= 0)?.value
    : selectList[0].items[0]?.value;

  const [value, setValue] = useState<string | undefined>(defaultValue);

  const handleRaceChange = useCallback(
    (raceId: string): void => {
      if (!eventUniqueCode || !raceId) {
        return;
      }
      setValue(raceId);
      if (onValueChange) {
        onValueChange(raceId);
      }

      // Only remember the last selected race if it's an actual subEvent
      if (event!.subEvents.find(({ id }) => `${id}` === raceId)) {
        dispatch({ type: 'setLastSelectedRace', payload: raceId });
      } else {
        // If we're on the awards page and we select all or multi and then
        // navigate to the leaderboard page, the last select race will be set to
        // the last race selected on the awards page. That might be jarring for
        // the user, so we reset it to undefined, which will cause the race
        // selector to default to the first race in the list when the the user
        // navigates to the leaderboard page. Commented out for now as we need
        // to decide if this is the best approach.
        // dispatch({ type: 'setLastSelectedRace', payload: undefined });
      }

      // Always navigate to the selected value (including prepended items like "all" or "multi")
      const cleanedParams = clearFilters(new URLSearchParams(search));
      const url = buildRaceUrl(eventUniqueCode, raceId, path, `?${cleanedParams.toString()}`);
      navigate(url);
    },
    [dispatch, event, eventUniqueCode, navigate, onValueChange, path, search]
  );

  // Hide the label on mobile and if visibleLabel is false, otherwise show it inline
  const desktopVisibleLabel = isMobile || visibleLabel === false ? 'hidden' : 'inline';

  // Show skeleton while loading
  if (loading) {
    return (
      <DivSkeleton data-testid="race-selector-skeleton" className="w-[250px] h-10 rounded-md" />
    );
  }

  // Show disabled select with message if no races
  if (!hasRaces) {
    return (
      <Select
        id={'races-selector'}
        disabled
        triggerLabel={t('disabled_select_placeholder')}
        inlineLabel={true}
        selectLabel={{
          children: t('select_race_label'),
          htmlFor: 'races-selector',
          variant: desktopVisibleLabel,
          labelSize: 'md',
        }}
        selectList={[{ items: [] }]}
        height={'tall'}
        width={selectDefaultWidth}
      />
    );
  }

  return (
    <Select
      id={`races-selector-${eventUniqueCode}`}
      defaultValue={defaultValue}
      value={value}
      onValueChange={handleRaceChange}
      triggerLabel={t('select_race_label')}
      inlineLabel={true}
      selectLabel={{
        children: t('select_race_label'),
        htmlFor: `races-selector-${eventUniqueCode}`,
        variant: desktopVisibleLabel,
        labelSize: 'md',
      }}
      selectList={selectList}
      height={'tall'}
      width={selectDefaultWidth}
    />
  );
};

RaceSelector.displayName = 'RaceSelector';

export default RaceSelector;
